import React, { useEffect, useMemo, useState } from 'react';
import apiClient, { buildApiUrl } from '../config/api';
import Navbar from '../components/Navbar';
import { styles } from '../styles/newstyles';
import '../styles/OwnerDetail.css';
import '../styles/cao.css';

const DEFAULT_YEAR = 2026;
const CHIEF_CATEGORY_ID = 4;      // fixed category "Chief"
const CONTRACTS_CATEGORY_ID = 5;  // fixed category "Contracts"
const HIDDEN_NON_ADMIN_PROTEST_TYPE_IDS = new Set([4, 5]);
const UP_ARROW = '\u25B2';
const DOWN_ARROW = '\u25BC';

const FILE_TYPE_LABELS = {
  'application/pdf': 'PDF',
  'image/*': 'Image',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'message/rfc822': 'Email'
};

const EXTENSION_LABELS = {
  pdf: 'PDF',
  doc: 'Word',
  docx: 'Word',
  xls: 'Excel',
  xlsx: 'Excel',
  png: 'Image',
  jpg: 'Image',
  jpeg: 'Image',
  tif: 'Image',
  tiff: 'Image',
  eml: 'Email'
};

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US');

const formatCalendarDate = (value) => {
  if (!value) return '-';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return DATE_FORMATTER.format(new Date(Number(year), Number(month) - 1, Number(day)));
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : DATE_FORMATTER.format(parsed);
};

const formatDateOnly = (value) => {
  if (!value) return '-';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${month}/${day}/${year}`;
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  const year = parsed.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

const toSortableDateValue = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return Date.UTC(Number(year), Number(month) - 1, Number(day));
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const CaoPage = ({ onLogout }) => {
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [restricted, setRestricted] = useState(false); // Contracts access for any user
  const [chiefCountyId, setChiefCountyId] = useState(null);
  const [contractsCountyId, setContractsCountyId] = useState(null);
  const [counties, setCounties] = useState([]);

  const [year, setYear] = useState(DEFAULT_YEAR);
  const [typeId, setTypeId] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [contractsTypeId, setContractsTypeId] = useState(null);
  const [contractsTypeOptions, setContractsTypeOptions] = useState([]);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [contractsRecords, setContractsRecords] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsError, setContractsError] = useState('');

  // Protest tracking
  const [protestCountyId, setProtestCountyId] = useState(null);
  const [protestYear, setProtestYear] = useState(DEFAULT_YEAR);
  const [protestTypeId, setProtestTypeId] = useState(null);
  const [protestTypeOptions, setProtestTypeOptions] = useState([]);
  const [protestStatusOptions, setProtestStatusOptions] = useState([]);
  const [protestClientStatusOptions, setProtestClientStatusOptions] = useState([]);
  const [protestStatusCode, setProtestStatusCode] = useState('');
  const [protestClientStatusCode, setProtestClientStatusCode] = useState('');
  const [assignedOptions, setAssignedOptions] = useState([]);
  const [assignedName, setAssignedName] = useState('');
  const [protestRecords, setProtestRecords] = useState([]);
  const [protestLoading, setProtestLoading] = useState(false);
  const [protestError, setProtestError] = useState('');
  const [protestCollapsed, setProtestCollapsed] = useState(false);
  const [protestSortConfig, setProtestSortConfig] = useState({ column: null, direction: 'asc' });
  const [protestAttachments, setProtestAttachments] = useState([]);
  const [protestAttachmentsLoading, setProtestAttachmentsLoading] = useState(false);
  const [protestAttachmentsError, setProtestAttachmentsError] = useState('');
  const [showProtestAttachmentsModal, setShowProtestAttachmentsModal] = useState(false);
  const [selectedProtestRow, setSelectedProtestRow] = useState(null);

  // Collapsible UI
  const [chiefCollapsed, setChiefCollapsed] = useState(false);
  const [contractsCollapsed, setContractsCollapsed] = useState(false);

  useEffect(() => {
    const loadSession = async () => {
      try {
        const sess = await apiClient.get('/auth/session', { withCredentials: true });
        const admin = !!sess.data?.isAdmin;
        setUsername(sess.data?.username || '');
        setIsAdmin(admin);
        setRestricted(Boolean(sess.data?.restricted)); // flag controls Contracts visibility
        const countiesRes = await apiClient.get('/lookup/counties', { withCredentials: true });
        setCounties(countiesRes.data || []);

        if (!admin) {
          // support both countyID and CountyID shapes from session
          const sessionCounty = sess.data?.countyID ?? sess.data?.CountyID ?? null;
          setChiefCountyId(sessionCounty);
          setContractsCountyId(sessionCounty);
          setProtestCountyId(sessionCounty);
        }
      } catch (err) {
        console.error('Failed to load session for CAO page:', err);
      }
    };

    loadSession();
  }, []);

  // Load types for fixed Chief category
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await apiClient.get(`/lookup/types/${CHIEF_CATEGORY_ID}`, { withCredentials: true });
        const opts = Array.isArray(res.data) ? res.data : [];
        setTypeOptions(opts);
        if (opts.length) setTypeId(opts[0].ID ?? opts[0].id ?? null);
      } catch (err) {
        console.error('Failed to load types for Chief category:', err);
        setTypeOptions([]);
        setTypeId(null);
      }
    };
    fetchTypes();
  }, []);

  // Load types for Contracts category when allowed
  useEffect(() => {
    if (!restricted) return;
    const fetchTypes = async () => {
      try {
        const res = await apiClient.get(`/lookup/types/${CONTRACTS_CATEGORY_ID}`, { withCredentials: true });
        const opts = Array.isArray(res.data) ? res.data : [];
        setContractsTypeOptions(opts);
        if (opts.length) setContractsTypeId(opts[0].ID ?? opts[0].id ?? null);
      } catch (err) {
        console.error('Failed to load types for Contracts category:', err);
        setContractsTypeOptions([]);
        setContractsTypeId(null);
      }
    };
    fetchTypes();
  }, [restricted, isAdmin]);

  // Protest lookups
  useEffect(() => {
    const fetchProtestTypes = async () => {
      try {
        const res = await apiClient.get('/lookup/protest-types', { withCredentials: true });
        const opts = Array.isArray(res.data) ? res.data : [];
        setProtestTypeOptions(opts);
      } catch (err) {
        console.error('Failed to load protest types:', err);
        setProtestTypeOptions([]);
        setProtestTypeId(null);
      }
    };
    fetchProtestTypes();
  }, []);

  useEffect(() => {
    const fetchProtestStatuses = async () => {
      try {
        const res = await apiClient.get('/lookup/protest-statuses', { withCredentials: true });
        const rows = Array.isArray(res.data) ? res.data : [];

        const statusMap = new Map();
        const clientMap = new Map();
        const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
        const isEnabledFlag = (value) => value === true || value === 1 || value === '1';

        rows.forEach((row) => {
          const code = row.Code ?? row.StatusCode ?? row.code;
          const description = row.Description ?? row.StatusDescription ?? row.description;
          const clientDesc = row.ClientDescription ?? row.clientDescription ?? row.ClientStatus ?? null;
          const internalStatus = row.InternalStatus ?? row.internalStatus ?? 0;
          const externalStatus = row.ExternalStatus ?? row.externalStatus ?? 0;
          const hasInternalFlag = row.InternalStatus !== undefined || row.internalStatus !== undefined;
          const hasExternalFlag = row.ExternalStatus !== undefined || row.externalStatus !== undefined;

          if ((!hasInternalFlag || isEnabledFlag(internalStatus)) && hasValue(code) && hasValue(description) && !statusMap.has(code)) {
            statusMap.set(code, description);
          }

          if ((!hasExternalFlag || isEnabledFlag(externalStatus)) && hasValue(code) && hasValue(clientDesc)) {
            const key = `${code}::${clientDesc}`;
            if (!clientMap.has(key)) clientMap.set(key, { code, label: clientDesc });
          }
        });

        setProtestStatusOptions(Array.from(statusMap, ([code, label]) => ({ code, label })));
        setProtestClientStatusOptions(Array.from(clientMap.values()));
      } catch (err) {
        console.error('Failed to load protest statuses:', err);
        setProtestStatusOptions([]);
        setProtestClientStatusOptions([]);
      }
    };
    fetchProtestStatuses();
  }, []);

  const canFetch = useMemo(() => {
    const hasCounty = isAdmin ? !!chiefCountyId : chiefCountyId !== null && chiefCountyId !== undefined;
    return hasCounty && !!year && !!typeId;
  }, [isAdmin, chiefCountyId, year, typeId]);

  const canFetchContracts = useMemo(() => {
    if (!restricted) return false;
    const hasCounty = isAdmin ? !!contractsCountyId : contractsCountyId !== null && contractsCountyId !== undefined;
    return hasCounty && !!year && !!contractsTypeId;
  }, [restricted, isAdmin, contractsCountyId, year, contractsTypeId]);

  const canFetchProtests = useMemo(() => {
    const hasCounty = isAdmin ? !!protestCountyId : protestCountyId !== null && protestCountyId !== undefined;
    return hasCounty && !!protestYear;
  }, [isAdmin, protestCountyId, protestYear]);

  const visibleProtestTypeOptions = useMemo(() => {
    if (isAdmin) return protestTypeOptions;
    return protestTypeOptions.filter((t) => {
      const optionId = t.Id ?? t.ID ?? t.id;
      return !HIDDEN_NON_ADMIN_PROTEST_TYPE_IDS.has(Number(optionId));
    });
  }, [isAdmin, protestTypeOptions]);

  useEffect(() => {
    if (!isAdmin && protestStatusCode) {
      setProtestStatusCode('');
    }
  }, [isAdmin, protestStatusCode]);

  useEffect(() => {
    if (!isAdmin && protestTypeId && HIDDEN_NON_ADMIN_PROTEST_TYPE_IDS.has(Number(protestTypeId))) {
      setProtestTypeId(null);
    }
  }, [isAdmin, protestTypeId]);

  const sortedProtestRecords = useMemo(() => {
    if (!protestSortConfig.column) return protestRecords;

    const directionMultiplier = protestSortConfig.direction === 'asc' ? 1 : -1;
    const isDateColumn = protestSortConfig.column === 'ReceivedByTYP' || protestSortConfig.column === 'HearingDate';
    const isNumericColumn = protestSortConfig.column === 'AttachmentCount';

    const getSortValue = (row) => {
      switch (protestSortConfig.column) {
        case 'ReceivedByTYP':
          return toSortableDateValue(row.ReceivedByTYP);
        case 'OwnerName':
          return row.OwnerName || '';
        case 'ProtestTypeDescription':
          return row.ProtestTypeDescription
            || protestTypeOptions.find((t) => (t.Id ?? t.ID ?? t.id) === row.ProtestTypeID)?.Description
            || protestTypeOptions.find((t) => (t.Id ?? t.ID ?? t.id) === row.ProtestTypeID)?.Name
            || '';
        case 'HearingDate':
          return toSortableDateValue(row.HearingDate);
        case 'Status':
          return row.Status
            || protestStatusOptions.find((s) => s.code === (row.StatusCode || row.Status))?.label
            || '';
        case 'ClientStatus':
          return row.ClientStatus
            || protestClientStatusOptions.find((s) => s.code === (row.ClientStatusCode || row.ClientStatus))?.label
            || '';
        case 'AssignedTo':
          return row.AssignedToFullName || row.AssignedFullName || row.AssignedTo || '';
        case 'AttachmentCount':
          return Number(row.AttachmentCount ?? 0);
        default:
          return '';
      }
    };

    return [...protestRecords].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      const aMissing = aValue === null || aValue === undefined || aValue === '';
      const bMissing = bValue === null || bValue === undefined || bValue === '';

      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;

      if (isDateColumn) {
        if (aValue === bValue) return 0;
        return aValue > bValue ? directionMultiplier : -directionMultiplier;
      }

      if (isNumericColumn) {
        if (aValue === bValue) return 0;
        return aValue > bValue ? directionMultiplier : -directionMultiplier;
      }

      const comparison = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
      return comparison * directionMultiplier;
    });
  }, [protestRecords, protestSortConfig, protestTypeOptions, protestStatusOptions, protestClientStatusOptions]);

  const fetchRecords = async () => {
    if (!canFetch) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/owners/owner-attachments/cao-documents', {
        params: {
          countyId: chiefCountyId,
          appraisalYear: year,
          categoryId: CHIEF_CATEGORY_ID,
          typeId
        },
        withCredentials: true
      });
      setRecords(Array.isArray(res.data) ? res.data : res.data?.records || []);
    } catch (err) {
      console.error('Failed to fetch CAO documents:', err);
      setError('Unable to load CAO documents.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto fetch when all required filters are set
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, chiefCountyId, year, typeId]);

  const fetchContractsRecords = async () => {
    if (!canFetchContracts) return;
    setContractsLoading(true);
    setContractsError('');
    try {
      const res = await apiClient.get('/owners/owner-attachments/cao-documents', {
        params: {
          countyId: contractsCountyId,
          appraisalYear: year,
          categoryId: CONTRACTS_CATEGORY_ID,
          typeId: contractsTypeId
        },
        withCredentials: true
      });
      setContractsRecords(Array.isArray(res.data) ? res.data : res.data?.records || []);
    } catch (err) {
      console.error('Failed to fetch Contracts documents:', err);
      setContractsError('Unable to load Contracts documents.');
      setContractsRecords([]);
    } finally {
      setContractsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractsRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetchContracts, contractsCountyId, year, contractsTypeId]);

  const dedupeNames = (names) => {
    const seen = new Set();
    return names
      .filter((n) => {
        if (!n) return false;
        const trimmed = n.toString().trim();
        if (!trimmed || seen.has(trimmed)) return false;
        seen.add(trimmed);
        return true;
      })
      .sort((a, b) => a.localeCompare(b));
  };

  const fetchAssignees = async () => {
    if (!canFetchProtests) return;
    try {
      const res = await apiClient.get('/owners/protests/track', {
        params: {
          countyId: protestCountyId,
          appraisalYear: protestYear
        },
        withCredentials: true
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.records || [];
      const names = dedupeNames(rows.map((r) => r.AssignedToFullName || r.AssignedFullName || r.AssignedTo || r.Assignee || r.FullName));
      setAssignedOptions(names);
      if (assignedName && !names.includes(assignedName)) setAssignedName('');
    } catch (err) {
      console.error('Failed to fetch assignees for protest tracker:', err);
      setAssignedOptions([]);
    }
  };

  useEffect(() => {
    fetchAssignees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protestCountyId, protestYear, canFetchProtests]);

  const fetchProtestRecords = async () => {
    if (!canFetchProtests) return;
    setProtestLoading(true);
    setProtestError('');
    try {
      const res = await apiClient.get('/owners/protests/track', {
        params: {
          countyId: protestCountyId,
          appraisalYear: protestYear,
          protestTypeId: protestTypeId || null,
          statusCode: protestStatusCode || null,
          clientStatusCode: protestClientStatusCode || null,
          assignedFullName: assignedName || null
        },
        withCredentials: true
      });
      setProtestRecords(Array.isArray(res.data) ? res.data : res.data?.records || []);
    } catch (err) {
      console.error('Failed to fetch protest tracking data:', err);
      setProtestError('Unable to load protest records.');
      setProtestRecords([]);
    } finally {
      setProtestLoading(false);
    }
  };

  useEffect(() => {
    fetchProtestRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetchProtests, protestCountyId, protestYear, protestTypeId, protestStatusCode, protestClientStatusCode, assignedName]);

  const renderTypeName = (id) => {
    const match = typeOptions.find((t) => (t.ID ?? t.id) === id);
    return match?.Name || match?.name || id;
  };

  const renderContractsTypeName = (id) => {
    const match = contractsTypeOptions.find((t) => (t.ID ?? t.id) === id);
    return match?.Name || match?.name || id;
  };

  const renderProtestTypeName = (id) => {
    const match = protestTypeOptions.find((t) => (t.Id ?? t.ID ?? t.id) === id);
    return match?.Description || match?.Name || match?.name || id || '-';
  };

  const renderStatusLabel = (value) => {
    if (!value) return '-';
    const match = protestStatusOptions.find((s) => s.code === value);
    return match?.label || value;
  };

  const renderClientStatusLabel = (value) => {
    if (!value) return '-';
    const match = protestClientStatusOptions.find((s) => s.code === value);
    return match?.label || value;
  };

  const renderCountyName = (sigmaGeoNbr) => {
    if (!sigmaGeoNbr) return '-';
    const match = counties.find((c) => {
      const countyId = c.CountyID ?? c.id ?? c.Id;
      return countyId === Number(sigmaGeoNbr) || String(countyId) === String(sigmaGeoNbr);
    });
    return match?.Name || match?.CountyName || sigmaGeoNbr;
  };

  const shouldShowChief = !isAdmin || !!chiefCountyId;
  const protestStatusLabel = 'TYP Status';
  const protestClientStatusLabel = isAdmin ? 'Client Status' : 'Status';

  const handleProtestSort = (column) => {
    setProtestSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const renderProtestSortableHeader = (columnKey, label) => {
    const isActive = protestSortConfig.column === columnKey;
    const isAscending = isActive && protestSortConfig.direction === 'asc';
    const isDescending = isActive && protestSortConfig.direction === 'desc';
    const ariaSort = isActive ? (isAscending ? 'ascending' : 'descending') : 'none';

    return (
      <th aria-sort={ariaSort} className="cao-sortable-th">
          <button
            type="button"
            className={'cao-sort-header-button' + (isActive ? ' active' : '')}
            onClick={() => handleProtestSort(columnKey)}
            aria-label={`Sort ${label}`}
          >
            <span>{label}</span>
            <span className={'sort-arrow-group cao-sort-arrow-group' + (isActive ? ' active' : '')}>
            <span className={'sort-arrow cao-sort-arrow sort-arrow--up' + (isAscending ? ' active' : '')}>{UP_ARROW}</span>
            <span className={'sort-arrow cao-sort-arrow sort-arrow--down' + (isDescending ? ' active' : '')}>{DOWN_ARROW}</span>
            </span>
          </button>
      </th>
    );
  };

  const friendlyFileType = (fileType, fileName) => {
    const normalized = (fileType || '').toLowerCase();
    if (normalized.startsWith('image/')) return 'Image';
    if (FILE_TYPE_LABELS[normalized]) return FILE_TYPE_LABELS[normalized];
    if (normalized.includes('excel')) return 'Excel';
    if (normalized.includes('word')) return 'Word';
    if (normalized.includes('pdf')) return 'PDF';
    if (normalized.includes('message/rfc822') || normalized.includes('email')) return 'Email';

    if (fileName) {
      const match = /\\.([^.]+)$/.exec(fileName);
      const ext = match?.[1]?.toLowerCase();
      if (ext && EXTENSION_LABELS[ext]) return EXTENSION_LABELS[ext];
    }
    return fileType || '-';
  };

  const openFileInNewTab = (row) => {
    if (!row?.FileID) return;
    setError('');
    try {
      const categoryParam = row.AttachmentCategoryID
        || (restricted ? CONTRACTS_CATEGORY_ID : CHIEF_CATEGORY_ID);
      const countyParam = categoryParam === CONTRACTS_CATEGORY_ID ? contractsCountyId : chiefCountyId;
      const url = buildApiUrl(
        `owners/owner-attachments/cao-documents/file?fileId=${row.FileID}`
        + `${countyParam ? `&countyId=${countyParam}` : ''}`
        + `${categoryParam ? `&categoryId=${categoryParam}` : ''}`
      );
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.error('Popup blocked while trying to open the document.');
      }
    } catch (err) {
      console.error('Failed to open file', err);
      setError('Unable to open file.');
    }
  };

  const openProtestAttachmentFile = (attachment) => {
    if (!selectedProtestRow?.ProtestID || !attachment?.AttachmentID) return;

    try {
      const url = buildApiUrl(
        `owners/protests/attachments/file?protestId=${selectedProtestRow.ProtestID}&attachmentId=${attachment.AttachmentID}`
      );
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.error('Popup blocked while trying to open the protest attachment.');
      }
    } catch (err) {
      console.error('Failed to open protest attachment', err);
      setProtestAttachmentsError('Unable to open attachment.');
    }
  };

  const handleOpenProtestAttachments = async (row) => {
    if (!row?.ProtestID || protestAttachmentsLoading) return;

    setSelectedProtestRow(row);
    setShowProtestAttachmentsModal(true);
    setProtestAttachmentsLoading(true);
    setProtestAttachmentsError('');

    try {
      const res = await apiClient.get('/owners/protests/attachments', {
        params: { protestId: row.ProtestID },
        withCredentials: true
      });
      setProtestAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch protest attachments:', err);
      setProtestAttachments([]);
      setProtestAttachmentsError('Unable to load protest attachments.');
    } finally {
      setProtestAttachmentsLoading(false);
    }
  };

  const closeProtestAttachmentsModal = () => {
    setShowProtestAttachmentsModal(false);
    setProtestAttachments([]);
    setProtestAttachmentsError('');
    setSelectedProtestRow(null);
  };

  return (
    <div style={{ ...styles.mainContainer, margin: '25px auto' }} className="cao-page">
      <Navbar username={username} isAdmin={isAdmin} onLogout={onLogout} />

      <div className="owner-container cao-container">
        <h2 style={styles.heading}>CHIEF DASHBOARD </h2>

        {/* Chief section */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              onClick={() => setChiefCollapsed((v) => !v)}
              className="view-button"
              style={{ padding: '6px 10px' }}
              aria-label={chiefCollapsed ? 'Expand section' : 'Collapse section'}
            >
              {chiefCollapsed ? '▲' : '▼'}
            </button>
          </div>

          {!chiefCollapsed && (
            <>
              <div className="owner-filters">
                {isAdmin && (
                  <div className="owner-section owner-section--county">
                    <label className="owner-select-label" htmlFor="cao-county-chief">County:</label>
                    <select
                      id="cao-county-chief"
                      className="owner-select owner-select--county"
                      value={chiefCountyId || ''}
                      onChange={(e) => setChiefCountyId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">-- Select County --</option>
                      {counties.map((c) => (
                        <option key={c.CountyID} value={c.CountyID}>{c.Name || c.CountyName}</option>
                      ))}
                    </select>
                    {!chiefCountyId && (
                      <p className="owner-no-records" style={{ marginTop: 8 }}>
                        Please select a county.
                      </p>
                    )}
                  </div>
                )}

                {shouldShowChief && (
                  <div className="owner-controls cao-controls">
                    <div className="owner-control owner-control--year">
                      <label className="owner-select-label" htmlFor="cao-year">Appraisal Year:</label>
                      <select
                        id="cao-year"
                        className="owner-select"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                      >
                        {Array.from({ length: 7 }, (_, i) => 2026 - i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="cao-type">Type (Chief):</label>
                      <select
                        id="cao-type"
                        className="owner-select"
                        value={typeId ?? ''}
                        onChange={(e) => setTypeId(e.target.value ? Number(e.target.value) : null)}
                        disabled={!typeOptions.length}
                      >
                        {typeOptions.map((t) => {
                          const optionId = t.ID ?? t.id;
                          const optionName = t.Name ?? t.name;
                          return <option key={optionId} value={optionId}>{optionName}</option>;
                        })}
                        {!typeOptions.length && <option value="">No types available</option>}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {shouldShowChief && (
                <div className="owner-table-wrapper">
                  {loading && <p style={{ marginTop: 20 }}>Loading data...</p>}
                  {!loading && error && (
                    <p className="owner-inline-error owner-inline-error--block">{error}</p>
                  )}
                  {!loading && !error && records.length === 0 && canFetch && (
                    <p className="owner-no-records">No records found{year ? ` for ${year}` : ''}.</p>
                  )}
                  {!loading && !error && records.length > 0 && (
                    <table className="owner-table cao-table">
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>File Type</th>
                          <th className="text-center">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row) => (
                          <tr key={row.FileID || `${row.FileName}-${row.UploadDate}`}>
                            <td>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={isAdmin && Boolean(row.IsAdminOnly) ? { color: '#e11d48', fontWeight: 600 } : {}}>
                                  {row.FileName || '-'}
                                </span>
                                {isAdmin && Boolean(row.IsAdminOnly) && (
                                  <em style={{ color: '#e11d48', fontSize: 14, fontStyle: 'italic', fontWeight: 700 }}>Int</em>
                                )}
                              </span>
                            </td>
                            <td>{row.Description || '-'}</td>
                            <td>{renderTypeName(row.AttachmentTypeID)}</td>
                            <td>{friendlyFileType(row.FileType, row.FileName)}</td>
                            <td className="text-center">
                              {row.FileID ? (
                                <button
                                  type="button"
                                  className="view-button"
                                  onClick={() => openFileInNewTab(row)}
                                >
                                  View
                                </button>
                              ) : (
                                <span style={{ color: '#888' }}>No File</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Contracts section (visible when restricted flag is true, for any user) */}
        {restricted && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <h3 style={{ ...styles.heading, marginTop: 0, marginBottom: 0, flex: 1, textAlign: 'center' }}>Contracts</h3>
              <button
                type="button"
                onClick={() => setContractsCollapsed((v) => !v)}
                className="view-button"
                style={{ padding: '6px 10px' }}
              >
                {contractsCollapsed ? '▲' : '▼'}
              </button>
            </div>

            {!contractsCollapsed && (
              <>
                <div className="owner-filters">
                  {isAdmin && (
                    <div className="owner-section owner-section--county">
                      <label className="owner-select-label" htmlFor="cao-county-contracts">County:</label>
                      <select
                        id="cao-county-contracts"
                        className="owner-select owner-select--county"
                        value={contractsCountyId || ''}
                        onChange={(e) => setContractsCountyId(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">-- Select County --</option>
                        {counties.map((c) => (
                          <option key={c.CountyID} value={c.CountyID}>{c.Name || c.CountyName}</option>
                        ))}
                      </select>
                      {!contractsCountyId && (
                        <p className="owner-no-records" style={{ marginTop: 8 }}>
                          Please select a county.
                        </p>
                      )}
                    </div>
                  )}

                  {(!isAdmin || contractsCountyId) && (
                    <div className="owner-controls cao-controls">
                      <div className="owner-control owner-control--year">
                        <label className="owner-select-label" htmlFor="contracts-year">Appraisal Year:</label>
                        <select
                          id="contracts-year"
                          className="owner-select"
                          value={year}
                          onChange={(e) => setYear(Number(e.target.value))}
                        >
                          {Array.from({ length: 7 }, (_, i) => 2026 - i).map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>

                      <div className="owner-control">
                        <label className="owner-select-label" htmlFor="contracts-type">Type (Contracts):</label>
                        <select
                          id="contracts-type"
                          className="owner-select"
                          value={contractsTypeId ?? ''}
                          onChange={(e) => setContractsTypeId(e.target.value ? Number(e.target.value) : null)}
                          disabled={!contractsTypeOptions.length}
                        >
                          {contractsTypeOptions.map((t) => {
                            const optionId = t.ID ?? t.id;
                            const optionName = t.Name ?? t.name;
                            return <option key={optionId} value={optionId}>{optionName}</option>;
                          })}
                          {!contractsTypeOptions.length && <option value="">No types available</option>}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {(!isAdmin || contractsCountyId) && (
                  <div className="owner-table-wrapper">
                    {contractsLoading && <p style={{ marginTop: 20 }}>Loading data...</p>}
                    {!contractsLoading && contractsError && (
                      <p className="owner-inline-error owner-inline-error--block">{contractsError}</p>
                    )}
                    {!contractsLoading && !contractsError && contractsRecords.length === 0 && canFetchContracts && (
                      <p className="owner-no-records">No records found{year ? ` for ${year}` : ''}.</p>
                    )}
                    {!contractsLoading && !contractsError && contractsRecords.length > 0 && (
                      <table className="owner-table cao-table">
                        <thead>
                          <tr>
                            <th>File Name</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th>File Type</th>
                            <th className="text-center">View</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contractsRecords.map((row) => (
                            <tr key={row.FileID || `${row.FileName}-${row.UploadDate}`}>
                              <td>{row.FileName || '-'}</td>
                              <td>{row.Description || '-'}</td>
                              <td>{renderContractsTypeName(row.AttachmentTypeID)}</td>
                              <td>{friendlyFileType(row.FileType, row.FileName)}</td>
                              <td className="text-center">
                                {row.FileID ? (
                                  <button
                                    type="button"
                                    className="view-button"
                                    onClick={() => openFileInNewTab(row)}
                                  >
                                    View
                                  </button>
                                ) : (
                                  <span style={{ color: '#888' }}>No File</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Protest tracker */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <h3 style={{ ...styles.heading, marginTop: 0, marginBottom: 0, flex: 1, textAlign: 'center' }}>Protest Tracker</h3>
            <button
              type="button"
              onClick={() => setProtestCollapsed((v) => !v)}
              className="view-button"
              style={{ padding: '6px 10px' }}
            >
              {protestCollapsed ? '▲' : '▼'}
            </button>
          </div>

          {!protestCollapsed && (
            <>
              <div className="owner-filters">
                {isAdmin && (
                  <div className="owner-section owner-section--county">
                    <label className="owner-select-label" htmlFor="protest-county">County:</label>
                    <select
                      id="protest-county"
                      className="owner-select owner-select--county"
                      value={protestCountyId || ''}
                      onChange={(e) => {
                        const val = e.target.value ? Number(e.target.value) : null;
                        setProtestCountyId(val);
                        setAssignedName('');
                      }}
                    >
                      <option value="">-- Select County --</option>
                      {counties.map((c) => (
                        <option key={c.CountyID} value={c.CountyID}>{c.Name || c.CountyName}</option>
                      ))}
                    </select>
                    {!protestCountyId && (
                      <p className="owner-no-records" style={{ marginTop: 8 }}>
                        Please select a county.
                      </p>
                    )}
                  </div>
                )}

                {(!isAdmin || protestCountyId) && (
                  <div className="owner-controls cao-controls" style={{ flexWrap: 'wrap', gap: 12 }}>
                    <div className="owner-control owner-control--year">
                      <label className="owner-select-label" htmlFor="protest-year">Appraisal Year:</label>
                      <select
                        id="protest-year"
                        className="owner-select"
                        value={protestYear}
                        onChange={(e) => setProtestYear(Number(e.target.value))}
                      >
                        {Array.from({ length: 7 }, (_, i) => 2026 - i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>

                    {isAdmin && (
                      <div className="owner-control">
                        <label className="owner-select-label" htmlFor="protest-status">{protestStatusLabel}:</label>
                        <select
                          id="protest-status"
                          className="owner-select"
                          value={protestStatusCode}
                          onChange={(e) => setProtestStatusCode(e.target.value)}
                          disabled={!protestStatusOptions.length}
                        >
                          <option value="">All</option>
                          {protestStatusOptions.map((s) => (
                            <option key={s.code} value={s.code}>{s.label}</option>
                          ))}
                          {!protestStatusOptions.length && <option value="">No TYP statuses available</option>}
                        </select>
                      </div>
                    )}

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="protest-client-status">{protestClientStatusLabel}:</label>
                      <select
                        id="protest-client-status"
                        className="owner-select"
                        value={protestClientStatusCode}
                        onChange={(e) => setProtestClientStatusCode(e.target.value)}
                        disabled={!protestClientStatusOptions.length}
                      >
                        <option value="">All</option>
                        {protestClientStatusOptions.map((s, idx) => (
                          <option key={`${s.code}-${idx}`} value={s.code}>{s.label}</option>
                        ))}
                        {!protestClientStatusOptions.length && (
                          <option value="">{isAdmin ? 'No client statuses available' : 'No statuses available'}</option>
                        )}
                      </select>
                    </div>

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="protest-type">Protest Type:</label>
                      <select
                        id="protest-type"
                        className="owner-select"
                        value={protestTypeId ?? ''}
                        onChange={(e) => setProtestTypeId(e.target.value ? Number(e.target.value) : null)}
                        disabled={!visibleProtestTypeOptions.length}
                      >
                        <option value="">All Types</option>
                        {visibleProtestTypeOptions.map((t) => {
                          const optionId = t.Id ?? t.ID ?? t.id;
                          const optionName = t.Description ?? t.Name ?? t.name;
                          return <option key={optionId} value={optionId}>{optionName}</option>;
                        })}
                        {!visibleProtestTypeOptions.length && <option value="">No protest types available</option>}
                      </select>
                    </div>

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="protest-assigned">Assigned:</label>
                      <select
                        id="protest-assigned"
                        className="owner-select"
                        value={assignedName}
                        onChange={(e) => setAssignedName(e.target.value)}
                      >
                        <option value="">All</option>
                        {assignedOptions.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                        {!assignedOptions.length && <option value="">No assignees for this county/year</option>}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {(!isAdmin || protestCountyId) && (
                <div className="owner-table-wrapper">
                  {protestLoading && <p style={{ marginTop: 20 }}>Loading data...</p>}
                  {!protestLoading && protestError && (
                    <p className="owner-inline-error owner-inline-error--block">{protestError}</p>
                  )}
                  {!protestLoading && !protestError && protestRecords.length === 0 && canFetchProtests && (
                    <p className="owner-no-records">No protest records found{protestYear ? ` for ${protestYear}` : ''}.</p>
                  )}
                  {!protestLoading && !protestError && protestRecords.length > 0 && (
                    <table className="owner-table cao-table cao-table--protests">
                      <thead>
                        <tr>
                          <th>County</th>
                          {renderProtestSortableHeader('ReceivedByTYP', 'Received BY Typ')}
                          {renderProtestSortableHeader('OwnerName', 'Owner')}
                          {renderProtestSortableHeader('ProtestTypeDescription', 'Protest Type')}
                          {renderProtestSortableHeader('HearingDate', 'Hearing Date')}
                          {isAdmin && renderProtestSortableHeader('Status', protestStatusLabel)}
                          {renderProtestSortableHeader('ClientStatus', protestClientStatusLabel)}
                          {renderProtestSortableHeader('AssignedTo', 'Assigned To')}
                          {renderProtestSortableHeader('AttachmentCount', 'Count')}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedProtestRecords.map((row, idx) => (
                          <tr key={row.ProtestID || row.AccountNumber || row.SigmaGeoNbr || idx}>
                            <td>{renderCountyName(row.SigmaGeoNbr)}</td>
                            <td>{formatDateOnly(row.ReceivedByTYP)}</td>
                            <td>{row.OwnerName || '-'}</td>
                            <td>{row.ProtestTypeDescription || renderProtestTypeName(row.ProtestTypeID)}</td>
                            <td>{formatCalendarDate(row.HearingDate)}</td>
                            {isAdmin && <td>{row.Status || renderStatusLabel(row.StatusCode || row.Status)}</td>}
                            <td>{row.ClientStatus || renderClientStatusLabel(row.ClientStatusCode || row.ClientStatus)}</td>
                            <td>{row.AssignedToFullName || row.AssignedFullName || row.AssignedTo || '-'}</td>
                            <td className="text-center">
                              {Number(row.AttachmentCount ?? 0) > 0 ? (
                                <button
                                  type="button"
                                  className="file-count-cell cao-count-button"
                                  onClick={() => handleOpenProtestAttachments(row)}
                                >
                                  {row.AttachmentCount}
                                </button>
                              ) : (
                                <span className="cao-count-value">0</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showProtestAttachmentsModal && (
        <div className="attachment-modal-overlay">
          <div className="attachment-modal cao-protest-attachment-modal">
            <h3 className="modal-title">Protest Attachments</h3>
            <button className="close-button" onClick={closeProtestAttachmentsModal}>×</button>

            {selectedProtestRow && (
              <div className="modal-heading">
                <span className="modal-owner-name">
                  {selectedProtestRow.OwnerName || `Protest ${selectedProtestRow.ProtestID}`}
                </span>
              </div>
            )}

            {protestAttachmentsLoading ? (
              <div className="spinner-container">
                <div className="spinner"></div>
                <p>Loading attachments...</p>
              </div>
            ) : protestAttachmentsError ? (
              <p className="owner-inline-error owner-inline-error--block">{protestAttachmentsError}</p>
            ) : protestAttachments.length === 0 ? (
              <p className="owner-no-records">No attachments found.</p>
            ) : (
              <table className="owner-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {protestAttachments.map((attachment) => (
                    <tr key={attachment.AttachmentID}>
                      <td>{attachment.AttachmentTypeDescription || attachment.AttachmentType || '-'}</td>
                      <td>{attachment.Description || '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="view-button"
                          onClick={() => openProtestAttachmentFile(attachment)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CaoPage;

